import {
  BLOCKED_ORDER_OPTION_KEYS,
  DOA_OPTION_BANDS,
  getDoaOption,
  INITIATIVE_DOA_OPTION_KEY,
  isBlockedOrderOptionKey,
  MASS_DOA_OPTION_KEY,
  SINGLE_DOA_OPTION_KEYS,
  type DoaOptionKey,
  type DoaRiskClass,
  type DoaType,
} from './delegation-of-authority'
import { RCL_MULTIPLIER_RISK_CLASS_CODES } from './rcl-multiplier-mappings'
import { isRiskClassCode, type RiskClassCode } from './risk-classes'

// ── Statuses ─────────────────────────────────────────────────────────────────────────────────
// Shared by the review row and its versions. A review is "active" while it can still move.
export const CLR_STATUSES = ['in_review', 'sent_back_to_preparer', 'approved', 'rejected'] as const
export type ClrStatus = (typeof CLR_STATUSES)[number]

export const CLR_ACTIVE_STATUSES = ['in_review', 'sent_back_to_preparer'] as const
export type ClrActiveStatus = (typeof CLR_ACTIVE_STATUSES)[number]

/**
 * Statuses from which an unowned (system-prepared) review may be claimed and resubmitted by any
 * DOA-eligible preparer. Distinct from `CLR_ACTIVE_STATUSES` on purpose: this encodes the claim
 * policy, not liveness. `in_review` is included so a preparer can supply a missing deviation
 * justification before the reviewers act, rather than waiting for a send-back.
 */
export const CLR_CLAIMABLE_STATUSES: readonly ClrStatus[] = ['in_review', 'sent_back_to_preparer']

// ── Action history entries ───────────────────────────────────────────────────────────────────
export const CLR_ACTION_TYPES = [
  'submitted',
  'reviewed',
  'approved',
  'rejected',
  'sent_back',
] as const
export type ClrActionType = (typeof CLR_ACTION_TYPES)[number]

// The verbs a reviewer/approver can send to the actions endpoint. `advance` records `reviewed`
// at a reviewer slot and `approved` at the approver slot.
export const CLR_DECISIONS = ['advance', 'reject', 'send_back'] as const
export type ClrDecision = (typeof CLR_DECISIONS)[number]

// ── Chain slots ──────────────────────────────────────────────────────────────────────────────
// The actionable slots of the review chain, in order. The preparer acts through create/resubmit,
// never through the actions endpoint, so it is not part of this list.
export const CLR_CHAIN_SLOTS = ['reviewer_1', 'reviewer_2', 'approver'] as const
export type ClrChainSlot = (typeof CLR_CHAIN_SLOTS)[number]

// ── Business thresholds ──────────────────────────────────────────────────────────────────────
/** Reviews whose NEW credit limit is at/above this are "strategic": context and variance
 *  explanations required. (The commentary on the financial ratios lives on the statements
 *  themselves — see `google_financial_statements.comment` — not on the review.) */
export const CLR_STRATEGIC_ACCOUNT_THRESHOLD = 250_000

/**
 * Whether a single CLR is strategic, read off the PROPOSED (new) limit rather than the current one —
 * the same basis the DOA band is resolved on. What the extra justification is for is the limit being
 * granted, so an account at $2M proposed down to $150k is not strategic, and one at $100k proposed
 * up to $260k is.
 */
export function isClrStrategicAccount(proposedCreditLimit: number | null): boolean {
  return proposedCreditLimit !== null && proposedCreditLimit >= CLR_STRATEGIC_ACCOUNT_THRESHOLD
}

/**
 * Whether a recommendation puts an account out of mass CLR and into a single one.
 *
 * The same rule as `isClrStrategicAccount`, read off the RECOMMENDED limit because a mass line's
 * proposal is the recommendation until someone deviates it (and a deviation is capped below the
 * threshold). It cuts both ways: an account at a $2M limit recommended down to $150k belongs in a
 * mass run, and one at $100k recommended up to $260k does not.
 *
 * A null recommendation is not strategic. Nothing is being proposed, so the line enters the run
 * and resolves to "left unchanged".
 */
export function isMassClrStrategicRecommendation(recommendedCreditLimit: number | null): boolean {
  return (
    recommendedCreditLimit !== null && recommendedCreditLimit >= CLR_STRATEGIC_ACCOUNT_THRESHOLD
  )
}

// ── Assignable risk classes ──────────────────────────────────────────────────────────────────
/**
 * The risk classes a preparer may put on a CLR. Exactly the multiplier grid's own dimension: Z01
 * (provisional) and Z07 (block) have no multiplier row, so neither can produce a credit limit —
 * offering them would let a preparer pick a class the recommendation engine cannot price.
 */
export const CLR_SELECTABLE_RISK_CLASS_CODES = RCL_MULTIPLIER_RISK_CLASS_CODES
export type ClrSelectableRiskClassCode = (typeof CLR_SELECTABLE_RISK_CLASS_CODES)[number]

export function isClrSelectableRiskClassCode(
  value: string | null | undefined,
): value is ClrSelectableRiskClassCode {
  return value != null && (CLR_SELECTABLE_RISK_CLASS_CODES as readonly string[]).includes(value)
}

/** A submission weighed against the recommendation it was prepared from. */
export type ClrDeviationInput = {
  proposedCreditLimit: number
  recommendedCreditLimit: number | null
  proposedRiskClass: string | null
  recommendedRiskClass: string | null
}

/**
 * Whether a proposal deviates from the recommendation snapshot. Both system-calculated values count:
 * changing the credit limit OR the risk class is a deviation, since the risk class is what the
 * recommended limit is derived from. A null recommendation (not computable) means there is nothing to
 * lock the input to, so any proposal counts as a deviation and requires deviation authority + a reason.
 */
export function isDeviation(input: ClrDeviationInput): boolean {
  if (input.recommendedCreditLimit === null) return true
  if (input.proposedCreditLimit !== input.recommendedCreditLimit) return true
  return input.proposedRiskClass !== input.recommendedRiskClass
}

/** The DOA type a submission routes through — decided by what actually happened. */
export function deriveDoaTypeKey(input: ClrDeviationInput): DoaType {
  return isDeviation(input) ? 'single_with_deviation' : 'single_no_deviation'
}

// ── Evidences ────────────────────────────────────────────────────────────────────────────────
/** PDFs, the common image formats, the Office document formats, CSV, and saved Outlook messages.
 *  Shared by the presign schema (backend) and the dropzone validation (frontend) so both reject the
 *  same files. Macro-enabled Office files (.docm/.xlsm/.pptm) are deliberately absent — an evidence
 *  never needs to run code. */
export const CLR_EVIDENCE_CONTENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/bmp',
  'image/tiff',
  'image/heic',
  'image/heif',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-outlook',
] as const
export type ClrEvidenceContentType = (typeof CLR_EVIDENCE_CONTENT_TYPES)[number]

/** Total over `ClrEvidenceContentType`, so the MIME allowlist and the file-name pattern below
 *  can't drift apart. */
const CLR_EVIDENCE_EXTENSIONS_BY_CONTENT_TYPE = {
  'application/pdf': ['pdf'],
  'image/png': ['png'],
  'image/jpeg': ['jpg', 'jpeg'],
  'image/webp': ['webp'],
  'image/gif': ['gif'],
  'image/bmp': ['bmp'],
  'image/tiff': ['tif', 'tiff'],
  'image/heic': ['heic'],
  'image/heif': ['heif'],
  'application/msword': ['doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx'],
  'application/vnd.ms-excel': ['xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
  // A Windows machine with Excel installed reports a .csv as `application/vnd.ms-excel`, which is
  // already on the allowlist; the extension entry is what catches every other machine.
  'text/csv': ['csv'],
  'application/vnd.ms-powerpoint': ['ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['pptx'],
  // Browsers report no type at all for a saved Outlook message, so this one reaches the allowlist
  // through the extension fallback in `resolveClrEvidenceContentType` rather than `file.type`.
  'application/vnd.ms-outlook': ['msg'],
} as const satisfies Record<ClrEvidenceContentType, readonly string[]>

/** Dotted extensions for the file picker's `accept`, alongside the MIME types: a machine with no
 *  OS registration for a format offers nothing under a MIME-only filter. */
export const CLR_EVIDENCE_FILE_EXTENSIONS = Object.values(CLR_EVIDENCE_EXTENSIONS_BY_CONTENT_TYPE)
  .flat()
  .map((extension) => `.${extension}`)

/** Accepted file extensions, matched case-insensitively against the client file name. */
export const CLR_EVIDENCE_FILE_NAME_PATTERN = new RegExp(
  `\\.(${Object.values(CLR_EVIDENCE_EXTENSIONS_BY_CONTENT_TYPE).flat().join('|')})$`,
  'i',
)

const CLR_EVIDENCE_CONTENT_TYPE_BY_EXTENSION = new Map<string, ClrEvidenceContentType>(
  Object.entries(CLR_EVIDENCE_EXTENSIONS_BY_CONTENT_TYPE).flatMap(([contentType, extensions]) =>
    extensions.map((extension) => [extension, contentType as ClrEvidenceContentType] as const),
  ),
)

/**
 * The content type to upload a picked file as, or null when the file is not an accepted kind.
 *
 * Prefers what the browser reports, and falls back to the extension because a browser only knows
 * a file's type from an OS registration: the Office types in particular come back as `''` or
 * `application/octet-stream` on a machine without Office installed, which would otherwise reject
 * a perfectly ordinary .xlsx. The backend re-checks both the type and the extension, so the
 * fallback widens nothing it does not already accept.
 */
export function resolveClrEvidenceContentType(file: {
  type: string
  name: string
}): ClrEvidenceContentType | null {
  if ((CLR_EVIDENCE_CONTENT_TYPES as readonly string[]).includes(file.type)) {
    return file.type as ClrEvidenceContentType
  }
  const extension = file.name.match(CLR_EVIDENCE_FILE_NAME_PATTERN)?.[0].slice(1).toLowerCase()
  return (extension && CLR_EVIDENCE_CONTENT_TYPE_BY_EXTENSION.get(extension)) || null
}

/** Authored in MB (what the UI tells the user); the byte cap the schemas validate is derived. */
export const CLR_EVIDENCE_MAX_FILE_SIZE_MB = 50
export const CLR_EVIDENCE_MAX_FILE_SIZE_BYTES = CLR_EVIDENCE_MAX_FILE_SIZE_MB * 1024 * 1024

// ── Business-rule error keys ─────────────────────────────────────────────────────────────────
// Emitted as ZodIssue `params.type` so the frontend can translate them (i18n keys live under the
// credit module's `clr.errors` block) — same pattern as DOA_SAVE_ERRORS.
export const CLR_ERRORS = [
  'clrAlreadyActive',
  'clrNotEligiblePreparer',
  'clrUnresolvableBand',
  'clrRollingVarianceExplanationRequired',
  'clrContextExplanationRequired',
  'clrReasonForDeviationRequired',
  'clrCommentRequired',
  'clrNotEligibleForSlot',
  'clrInvalidState',
  'clrOnlyPreparerCanResubmit',
  'clrAlreadyClaimed',
  'clrSnapshotUnavailable',
  // The account holds a temporary uplift from a started initiative; a CLR would undo it.
  'clrBlockedByActiveInitiative',
  // The account is a line of a mass run that is in review or sent back: one review at a time.
  'clrHeldByMassReview',
  // A proposed limit with cents. Credit limits are whole dollars; shared with the mass line patch.
  'clrCreditLimitNotWhole',
] as const
export type ClrErrorType = (typeof CLR_ERRORS)[number]

/** Narrows an unknown `params.type` off an error body to a known key, so callers translate off a
 *  guard rather than an unchecked cast. */
export function isClrErrorType(value: unknown): value is ClrErrorType {
  return typeof value === 'string' && (CLR_ERRORS as readonly string[]).includes(value)
}

// ── SAP risk class code → DOA risk label ─────────────────────────────────────────────────────
// Only consulted where a band is split by risk class (every other band is 'all'). Z01
// (provisional) and Z07 (block) have no DOA row of their own; they, a missing code and any code we
// cannot read take `DOA_FALLBACK_RISK_CLASS` instead — see there.
export const DOA_RISK_CLASS_BY_RISK_CLASS_CODE: Record<
  RiskClassCode,
  Extract<DoaRiskClass, 'very_low' | 'low' | 'medium' | 'high'> | null
> = {
  Z06: 'very_low',
  Z04: 'low',
  Z03: 'medium',
  Z02: 'high',
  Z01: null,
  Z07: null,
}

/**
 * The risk class a customer is routed as when theirs has no DOA row: Z01 provisional, Z07 block,
 * none at all, or a code we cannot read. The highest one, so every band has a fallback and the
 * authority is the strictest the band offers — routing one level too high beats leaving the
 * record with nobody to decide it. Blocked orders also escalate such an account
 * (`resolveBlockedOrderDoaOptionKey`).
 */
export const DOA_FALLBACK_RISK_CLASS = 'high' satisfies DoaRiskClass

/** The customer's DOA risk class, or null when it has no DOA row and takes the fallback. */
export function toMappedDoaRiskClass(
  code: string | null,
): Extract<DoaRiskClass, 'very_low' | 'low' | 'medium' | 'high'> | null {
  return isRiskClassCode(code) ? DOA_RISK_CLASS_BY_RISK_CLASS_CODE[code] : null
}

// ── Hierarchy ────────────────────────────────────────────────────────────────────────────────
// DOA authority is hierarchical: an assignment at a band also covers every band below it. The
// five 50k–100k risk-class bands are siblings at one rank — holding one does NOT grant the others.
export const DOA_OPTION_RANK: Record<DoaOptionKey, number> = {
  all__lte_35k: 0,
  all__35k_50k: 1,
  very_low__50k_100k: 2,
  low__50k_100k: 2,
  medium__50k_100k: 2,
  high__50k_100k: 2,
  security_deposit__50k_100k: 2,
  all__100k_250k: 3,
  all__250k_1m: 4,
  all__1m_1_5m: 5,
  all__1_5m_5m: 6,
  all__gt_5m: 7,
  // Never read: both expanders short-circuit on the mass and initiative keys. Present only to
  // keep the record total.
  mass__lt_250k: -1,
  initiative__all: -1,

  // ── Blocked orders: a second, independent ladder ──────────────────────────────────────────
  // Ranks are compared only against other blocked-orders keys — the expanders short-circuit on the
  // prefix and filter `BLOCKED_ORDER_OPTION_KEYS`, so these numbers never meet the CLR ones.
  //
  // Rank follows the AMOUNT TIER alone. Within a tier the risk-class variants and the
  // standard/escalated twins are all siblings, so holding one never grants another. Ranking
  // escalated above standard would have leaked authority across risk classes too, since the
  // comparison is tier-wide and cannot see the risk class.
  blocked_orders__all__lte_35k: 1,
  blocked_orders__all__lte_35k__escalated: 1,
  blocked_orders__very_low__35k_100k: 2,
  blocked_orders__very_low__35k_100k__escalated: 2,
  blocked_orders__low__35k_100k: 2,
  blocked_orders__low__35k_100k__escalated: 2,
  blocked_orders__medium__35k_50k: 2,
  blocked_orders__medium__35k_50k__escalated: 2,
  blocked_orders__high__35k_50k: 2,
  blocked_orders__high__35k_50k__escalated: 2,
  blocked_orders__medium__50k_100k: 3,
  blocked_orders__high__50k_100k: 3,
  blocked_orders__all__100k_250k: 4,
  blocked_orders__all__250k_1m: 5,
  blocked_orders__all__1m_1_5m: 6,
  blocked_orders__all__1_5m_5m: 7,
  blocked_orders__all__gt_5m: 8,
}

/**
 * The option keys whose assignees may act on `resolvedKey`: the key itself plus every key of
 * strictly greater rank. `all__100k_250k` therefore covers every 50k–100k sibling, while
 * `low__50k_100k` covers the ≤50k bands when they resolve but never its own siblings.
 */
export function getCoveringDoaOptionKeys(resolvedKey: DoaOptionKey): DoaOptionKey[] {
  // Mass and initiative each have one option, so there is no hierarchy to expand: authority is an
  // exact match. Guarding here rather than at the call sites covers every expansion site at once
  // (`getEligibleSlotHolders`, `getUserActionableTuples`, `isUserEligibleForSlot`).
  if (resolvedKey === MASS_DOA_OPTION_KEY) return [MASS_DOA_OPTION_KEY]
  if (resolvedKey === INITIATIVE_DOA_OPTION_KEY) return [INITIATIVE_DOA_OPTION_KEY]
  // Blocked orders is a separate ladder with its own ranks. Without this branch the filter below
  // would return CLR keys for a blocked-orders band — eligibility computed against the wrong
  // matrix, with nothing to indicate it went wrong.
  if (isBlockedOrderOptionKey(resolvedKey)) {
    const blockedRank = DOA_OPTION_RANK[resolvedKey]
    return BLOCKED_ORDER_OPTION_KEYS.filter(
      (key) => key === resolvedKey || DOA_OPTION_RANK[key] > blockedRank,
    )
  }
  const rank = DOA_OPTION_RANK[resolvedKey]
  // `SINGLE_DOA_OPTION_KEYS`, so the mass key cannot appear in a single CLR's covering set
  // whatever rank it carries.
  return SINGLE_DOA_OPTION_KEYS.filter((key) => key === resolvedKey || DOA_OPTION_RANK[key] > rank)
}

/** Inverse of getCoveringDoaOptionKeys: the resolved keys an assignment at `assignedKey` covers. */
export function getCoveredDoaOptionKeys(assignedKey: DoaOptionKey): DoaOptionKey[] {
  if (assignedKey === MASS_DOA_OPTION_KEY) return [MASS_DOA_OPTION_KEY]
  if (assignedKey === INITIATIVE_DOA_OPTION_KEY) return [INITIATIVE_DOA_OPTION_KEY]
  if (isBlockedOrderOptionKey(assignedKey)) {
    const blockedRank = DOA_OPTION_RANK[assignedKey]
    return BLOCKED_ORDER_OPTION_KEYS.filter(
      (key) => key === assignedKey || DOA_OPTION_RANK[key] < blockedRank,
    )
  }
  const rank = DOA_OPTION_RANK[assignedKey]
  return SINGLE_DOA_OPTION_KEYS.filter((key) => key === assignedKey || DOA_OPTION_RANK[key] < rank)
}

// ── Band resolver ────────────────────────────────────────────────────────────────────────────
/**
 * Resolves the single-CLR DOA band for a credit limit and risk class. Which limit is the caller's
 * call: `resolveClrChainDoaOptionKey` passes the PROPOSED one, and that is the band a single CLR
 * routes on. Mass CLR passes the CURRENT one, only to stamp each line with an informational band
 * (refreshed when the run is submitted); a mass run routes on `MASS_DOA_OPTION_KEY`.
 *
 * Band bounds follow DOA_OPTION_BANDS semantics: min exclusive, max inclusive. A null limit is
 * treated as 0 (→ `all__lte_35k`). Inside the 50k–100k window the `security_deposit` band takes
 * precedence when the customer has security deposits posted; otherwise the risk class code picks
 * the band, and an unmappable code (Z01/Z07/unknown/null) takes `DOA_FALLBACK_RISK_CLASS`.
 *
 * Total: every customer resolves to a band.
 */
export function resolveDoaOptionKey(input: {
  creditLimit: number | null
  riskClassCode: string | null
  hasSecurityDeposit: boolean
}): DoaOptionKey {
  const limit = input.creditLimit ?? 0

  const window50k100k = DOA_OPTION_BANDS.low__50k_100k
  const isIn50k100kWindow =
    window50k100k.min !== null &&
    window50k100k.max !== null &&
    limit > window50k100k.min &&
    limit <= window50k100k.max

  if (isIn50k100kWindow) {
    if (input.hasSecurityDeposit) return 'security_deposit__50k_100k'
    const doaRiskClass = toMappedDoaRiskClass(input.riskClassCode) ?? DOA_FALLBACK_RISK_CLASS
    return `${doaRiskClass}__50k_100k`
  }

  // `SINGLE_DOA_OPTION_KEYS`: the mass row's bounds describe a recommended limit, not a single-CLR
  // band, so it must stay unreachable from an amount. The same list is what
  // keeps every blocked-orders band out of reach. The all-risk bands cover every amount from no
  // floor to no ceiling, so the fallback is unreachable.
  return matchAllRiskBandByAmount(limit, SINGLE_DOA_OPTION_KEYS) ?? 'all__gt_5m'
}

/**
 * The band a single CLR version routes through for review and approval: resolved from the PROPOSED
 * limit and risk class, not the customer's current segment. Per version, like the DOA type, since a
 * resubmission can change the proposal. Same arithmetic as `resolveDoaOptionKey`.
 */
export function resolveClrChainDoaOptionKey(input: {
  proposedCreditLimit: number
  proposedRiskClassCode: string | null
  hasSecurityDeposit: boolean
}): DoaOptionKey | null {
  return resolveDoaOptionKey({
    creditLimit: input.proposedCreditLimit,
    riskClassCode: input.proposedRiskClassCode,
    hasSecurityDeposit: input.hasSecurityDeposit,
  })
}

/**
 * Finds the `riskClass: 'all'` band among `keys` whose bounds contain `amount`, following
 * `DOA_OPTION_BANDS` semantics (min exclusive, max inclusive unless `isMaxExclusive`).
 *
 * Shared by `resolveDoaOptionKey` and the blocked-orders resolver — same arithmetic over two
 * different ladders. Risk-class-specific bands are skipped: callers that need them (the 50k–100k
 * CLR window, the blocked-orders 35k–100k tier) select those by risk class before calling here.
 */
export function matchAllRiskBandByAmount(
  amount: number,
  keys: readonly DoaOptionKey[],
): DoaOptionKey | null {
  for (const key of keys) {
    const band = DOA_OPTION_BANDS[key]
    if (band.riskClass !== 'all') continue
    const aboveMin = band.min === null || amount > band.min
    const isMaxExclusive = 'isMaxExclusive' in band && band.isMaxExclusive
    const belowMax = band.max === null || (isMaxExclusive ? amount < band.max : amount <= band.max)
    if (aboveMin && belowMax) return key
  }
  return null
}

// ── Chain construction ───────────────────────────────────────────────────────────────────────
/**
 * The ordered actionable chain for a (type, option): CLR_CHAIN_SLOTS filtered by the option's
 * reviewer flags. The approver is always present. Returns [] when the option does not exist for
 * the type: callers must treat that as "no chain, cannot proceed". In practice only a mismatched
 * pair returns [] — a mass key on a single type, or a band key on mass.
 */
export function getClrChainSlots(typeKey: DoaType, optionKey: DoaOptionKey): ClrChainSlot[] {
  const option = getDoaOption(typeKey, optionKey)
  if (!option) return []
  return CLR_CHAIN_SLOTS.filter((slot) => {
    if (slot === 'reviewer_1') return option.requiresReviewer1
    if (slot === 'reviewer_2') return option.requiresReviewer2
    return true
  })
}

/**
 * The first actionable slot of the (type, option) chain, or null when the option is absent from the
 * type's matrix. Both `single_*` types cover all 12 bands and `mass` covers `mass__lt_250k`, so a
 * null here means the caller paired a type with the other's key: a programming error, not
 * something a user can cause. `resolveDoaOptionKey` returning null is the sole unresolvable-band
 * condition for a single CLR.
 */
export function getFirstClrChainSlot(
  typeKey: DoaType,
  optionKey: DoaOptionKey,
): ClrChainSlot | null {
  return getClrChainSlots(typeKey, optionKey)[0] ?? null
}

/** The slot after `slot` in the chain, or null when `slot` is the last one (the approver). */
export function getNextClrSlot(
  typeKey: DoaType,
  optionKey: DoaOptionKey,
  slot: ClrChainSlot,
): ClrChainSlot | null {
  const chain = getClrChainSlots(typeKey, optionKey)
  const index = chain.indexOf(slot)
  if (index === -1) return null
  return chain[index + 1] ?? null
}

/**
 * Unanimity gate for options flagged `requiresAllToApprove`: approval completes once the number of
 * distinct users who approved reaches the number of PRIMARY approver holders. Delegate approvals
 * count toward the quota (a delegate substitutes for an absent primary). Isolated here so the
 * policy can be adjusted in one place.
 *
 * `Math.max(requiredCount, 1)` is a floor against a NONSENSICAL denominator, not a licence to
 * approve on one vote: with zero primaries it would let the first signature complete the run.
 * Callers must reject a zero denominator before they get here — `applyMassClrDecision` throws
 * `massClrNoApproversConfigured`. Do not "fix" the clamp without finding that guard.
 */
export function isClrApprovalComplete(approvedUserIds: string[], requiredCount: number): boolean {
  return new Set(approvedUserIds).size >= Math.max(requiredCount, 1)
}
